import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JuryAuthContext = createContext({ juror: null, loading: true, login: async () => {}, logout: async () => {} });

export const useJuryAuth = () => useContext(JuryAuthContext);

export const JuryAuthProvider = ({ children }) => {
  const [juror, setJuror] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    // Only probe jury auth on jury-chamber pages — public visitors don't need
    // to trigger a 401 on every navigation.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/jury')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/jury/me`, { withCredentials: true });
      setJuror(data);
    } catch {
      setJuror(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/jury/login`, { email, password }, { withCredentials: true });
    setJuror(data);
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/jury/logout`, {}, { withCredentials: true }); } catch {}
    setJuror(null);
  };

  return (
    <JuryAuthContext.Provider value={{ juror, loading, login, logout, refresh }}>
      {children}
    </JuryAuthContext.Provider>
  );
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  if (detail && detail.message) return detail.message;
  return String(detail);
}
